<!-- 分享紀錄列表的一個人（遞迴）：頭像、名字、位階、加入日期；有下線的顯示直屬人數，點一下展開 -->
<template>
  <div class="oln">
    <div
      class="oln__row"
      :class="{ 'oln__row--group': hasChildren, 'oln__row--hit': hit, 'oln__row--nested': depth > 0 }"
      :role="hasChildren ? 'button' : null"
      :tabindex="hasChildren ? 0 : null"
      :aria-expanded="hasChildren ? String(isOpen) : null"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <span class="oln__avatar" :style="avatar">{{ initial }}</span>

      <div class="oln__main">
        <div class="oln__name" v-html="hl(node.name)"></div>
        <div class="oln__meta">
          <span class="oln__grade" :class="{ 'oln__grade--member': plain }">{{ node.grade }}</span>
          <span v-if="node.date" class="oln__date">{{ node.date }} 加入</span>
        </div>
        <!-- 信箱只在搜尋時出現（搜尋可能是比對到信箱） -->
        <div v-if="query && node.email" class="oln__email" v-html="hl(node.email)"></div>
      </div>

      <template v-if="hasChildren">
        <span class="oln__count" :aria-label="`直屬 ${node.children.length} 人`">
          <v-icon size="14">mdi-account-multiple-outline</v-icon>
          {{ node.children.length }}
        </span>
        <v-icon size="22" class="oln__chevron" :class="{ 'oln__chevron--open': isOpen }">mdi-chevron-down</v-icon>
      </template>
    </div>

    <v-expand-transition>
      <div v-if="hasChildren" v-show="isOpen" class="oln__children">
        <OrgListNodeItem v-for="c in node.children" :key="c.guid" :node="c" :query="query" :depth="depth + 1" />
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import { avatarStyle, highlight, initialOf, isPlainMember, matchesQuery } from "./orgUtils";

export default {
  name: "OrgListNodeItem",
  props: {
    // buildOrgTree 產生的節點：{ guid, name, unnamed, email, grade, date, children }
    node: { type: Object, required: true },
    query: { type: String, default: "" },
    depth: { type: Number, default: 0 },
  },
  data: () => ({ isOpen: false }),
  computed: {
    hasChildren() {
      return Array.isArray(this.node.children) && this.node.children.length > 0;
    },
    hit() {
      return matchesQuery(this.node, this.query);
    },
    plain() {
      return isPlainMember(this.node.grade);
    },
    initial() {
      return initialOf(this.node.name);
    },
    avatar() {
      return avatarStyle(this.node.name, this.node.unnamed);
    },
  },
  watch: {
    // 有搜尋字就全部展開（才看得到比對到的人）；清空就收合
    query: {
      immediate: true,
      handler(q) {
        this.isOpen = !!(q && this.hasChildren);
      },
    },
  },
  methods: {
    toggle() {
      if (this.hasChildren) this.isOpen = !this.isOpen;
    },
    hl(text) {
      return highlight(text, this.query);
    },
  },
};
</script>

<style scoped>
.oln__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  min-height: 56px;
}

.oln__row--group {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.oln__row--group:active {
  background: #f6f5fb;
}

.oln__row--group:focus-visible {
  outline: 2px solid #7c4dff;
  outline-offset: -2px;
}

/* 搜尋比對到的人：淡黃底 */
.oln__row--hit {
  background: #fffbe6;
}

.oln__avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 15px;
  font-weight: 700;
}

.oln__row--nested .oln__avatar {
  width: 30px;
  height: 30px;
  font-size: 13px;
}

.oln__main {
  flex: 1 1 auto;
  min-width: 0;
}

.oln__name {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.87);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oln__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 3px;
}

.oln__grade {
  padding: 0 8px;
  border-radius: 999px;
  background: #157af0;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
}

.oln__grade--member {
  background: #eeeeee;
  color: rgba(0, 0, 0, 0.6);
}

.oln__date {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.oln__email {
  margin-top: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 直屬人數 */
.oln__count {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(124, 77, 255, 0.1);
  color: #5e35b1;
  font-size: 13px;
  font-weight: 600;
  line-height: 24px;
}

.oln__count .v-icon {
  color: inherit;
}

.oln__chevron {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.4) !important;
  transition: transform 0.2s ease;
}

.oln__chevron--open {
  transform: rotate(180deg);
}

/* 下一層：往右縮一格，左邊一條細線看得出是誰底下的 */
.oln__children {
  margin-left: 29px;
  border-left: 2px solid #ece9f7;
}

.oln__children .oln__children {
  margin-left: 26px;
}

::v-deep mark {
  background: #ffe066;
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}
</style>
